import type { RequestSettings } from '@core/entities/RequestSettings';

export type FieldKey = keyof RequestSettings;

export interface FieldDescriptor {
  key: FieldKey;
  label: string;
  description: string;
  keywords: string;
  control:
    | { type: 'switch' }
    | { type: 'select'; options: { label: string; value: string }[] }
    | { type: 'number'; min: number; max: number }
    | { type: 'text'; placeholder: string };
}

export const REQUEST_SETTINGS_FIELDS: FieldDescriptor[] = [
  {
    key: 'httpVersion',
    label: 'HTTP Version',
    description: 'The HTTP protocol version to negotiate with the server.',
    keywords: 'http version protocol h1 h2 h3',
    control: {
      type: 'select',
      options: [
        { label: 'Auto', value: 'auto' },
        { label: 'HTTP/1.0', value: '1.0' },
        { label: 'HTTP/1.1', value: '1.1' },
        { label: 'HTTP/2', value: '2.0' },
      ],
    },
  },
  {
    key: 'sslVerification',
    label: 'SSL Certificate Verification',
    description:
      'Verify the server’s TLS certificate chain and hostname. Disabling this is insecure — only use it against a server you control with a self-signed certificate.',
    keywords: 'ssl tls verify certificate insecure',
    control: { type: 'switch' },
  },
  {
    key: 'followRedirects',
    label: 'Automatically Follow Redirects',
    description:
      'Follow HTTP 3xx redirect responses. When off, the redirect response itself is returned.',
    keywords: 'redirect follow 3xx location',
    control: { type: 'switch' },
  },
  {
    key: 'followOriginalHttpMethod',
    label: 'Follow Original HTTP Method',
    description:
      'Preserve the original method (e.g. POST) across a redirect instead of switching to GET.',
    keywords: 'redirect method post get strict',
    control: { type: 'switch' },
  },
  {
    key: 'followAuthorizationHeader',
    label: 'Follow Authorization Header',
    description:
      'Keep the Authorization header when a redirect points to a different host. Off by default since this can leak credentials to an unintended host.',
    keywords: 'redirect authorization header credentials cross-host',
    control: { type: 'switch' },
  },
  {
    key: 'removeRefererOnRedirect',
    label: 'Remove Referer Header on Redirect',
    description: 'Strip the Referer header when following a redirect.',
    keywords: 'redirect referer header remove',
    control: { type: 'switch' },
  },
  {
    key: 'maxRedirects',
    label: 'Maximum Number of Redirects',
    description: 'How many redirects to follow before giving up.',
    keywords: 'redirect max limit count',
    control: { type: 'number', min: 0, max: 20 },
  },
  {
    key: 'encodeUrlAutomatically',
    label: 'Encode URL Automatically',
    description: 'Percent-encode the path, query parameters, and auth fields automatically.',
    keywords: 'url encode automatically percent',
    control: { type: 'switch' },
  },
  {
    key: 'disableCookieJar',
    label: 'Disable Cookie Jar',
    description: 'Don’t read from or write to this workspace’s stored cookies for this request.',
    keywords: 'cookie jar disable',
    control: { type: 'switch' },
  },
  {
    key: 'minTlsVersion',
    label: 'Minimum TLS Version',
    description: 'The oldest TLS protocol version the handshake is allowed to negotiate down to.',
    keywords: 'tls ssl protocol version handshake minimum',
    control: {
      type: 'select',
      options: [
        { label: 'Auto', value: 'auto' },
        { label: 'TLS 1.0', value: '1.0' },
        { label: 'TLS 1.1', value: '1.1' },
        { label: 'TLS 1.2', value: '1.2' },
        { label: 'TLS 1.3', value: '1.3' },
      ],
    },
  },
  {
    key: 'cipherSuite',
    label: 'Cipher Suite Selection',
    description:
      'An OpenSSL cipher list string (e.g. ECDHE-RSA-AES128-GCM-SHA256) to restrict the TLS handshake to.',
    keywords: 'cipher suite tls ssl openssl',
    control: { type: 'text', placeholder: 'e.g. ECDHE-RSA-AES128-GCM-SHA256' },
  },
];

export const UNSUPPORTED_SETTINGS_FIELDS: { key: string; label: string; keywords: string }[] = [
  {
    key: 'use_server_cipher_suite',
    label: 'Use Server Cipher Suite During Handshake',
    keywords: 'server cipher suite handshake',
  },
  { key: 'strict_http_parser', label: 'Enable Strict HTTP Parser', keywords: 'strict http parser' },
  { key: 'http3', label: 'HTTP/3', keywords: 'http3 quic' },
];
